import PageHeader from "@/components/PageHeader";
import Reveal from "@/components/Reveal";
import { Target, Eye, ShieldCheck, Users, Award, MapPin } from "lucide-react";
import { images, stats } from "@/mock/mock";

const values = [
  { icon: ShieldCheck, title: "Integrity first", text: "We handle sensitive evidence and data with rigorous confidentiality and a defensible chain of custody." },
  { icon: Users, title: "Client partnership", text: "We work as an extension of your team, translating complex risk into clear business decisions." },
  { icon: Award, title: "Proven expertise", text: "Certified analysts and examiners with years of frontline forensic and defensive experience." },
];

export default function About() {
  return (
    <>
      <PageHeader
        eyebrow="About"
        title="Bringing world-class cyber defense to Canada"
        subtitle="Myriad View Limited (Canada) extends decades of digital forensics and information-security expertise to organizations across the country, operating from Halifax, Nova Scotia."
      />

      <section className="py-20">
        <div className="mx-auto max-w-7xl px-6 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <div className="relative rounded-2xl overflow-hidden border border-white/10 glow-lime">
              <img src={images.itPros} alt="Our professionals" className="w-full h-[420px] object-cover" />
            </div>
          </Reveal>
          <Reveal delay={100}>
            <span className="text-sm font-semibold uppercase tracking-wider text-brand-lime">Who we are</span>
            <h2 className="mt-3 font-display text-3xl font-bold text-white">A software solution mastermind, now local to Canada</h2>
            <p className="mt-4 text-slate-400">We develop effective plans to secure your business and digital assets — boosting customer confidence and safeguarding your reputation. Our Canadian branch pairs a 24/7 Security Operations Center with elite digital-forensics and incident-response capabilities.</p>
            <p className="mt-4 text-slate-400">We believe the best way to serve you is to work with a small number of strategic partners proven to deliver the best in their fields, at the most advantageous prices.</p>
            <div className="mt-6 flex items-center gap-2 text-slate-300">
              <MapPin className="h-5 w-5 text-brand-lime" /> 256 Alabaster Way, Halifax, NS B3P 0G1
            </div>
            <p className="mt-3 text-sm text-slate-500">Registered in Nova Scotia · Business No. 751148578NS0001</p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 border-y border-white/10 bg-brand-navy2/40">
        <div className="mx-auto max-w-7xl px-6 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((s) => (
            <div key={s.label} className="text-center">
              <p className="font-display text-4xl font-bold text-brand-lime">{s.value}</p>
              <p className="mt-2 text-sm text-slate-400">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="py-20">
        <div className="mx-auto max-w-7xl px-6 grid md:grid-cols-2 gap-6 mb-6">
          <Reveal>
            <div className="h-full rounded-2xl border border-white/10 bg-brand-navy2/60 p-8">
              <div className="grid h-12 w-12 place-items-center rounded-xl bg-brand-lime/10 text-brand-lime"><Target className="h-6 w-6" /></div>
              <h3 className="mt-5 font-display text-xl font-semibold text-white">Our mission</h3>
              <p className="mt-3 text-slate-400">To make robust, around-the-clock cyber security accessible to every Canadian organization — turning security from a cost centre into a source of confidence and growth.</p>
            </div>
          </Reveal>
          <Reveal delay={100}>
            <div className="h-full rounded-2xl border border-white/10 bg-brand-navy2/60 p-8">
              <div className="grid h-12 w-12 place-items-center rounded-xl bg-brand-cyan/10 text-brand-cyan"><Eye className="h-6 w-6" /></div>
              <h3 className="mt-5 font-display text-xl font-semibold text-white">Our vision</h3>
              <p className="mt-3 text-slate-400">A digital economy where businesses innovate fearlessly, backed by intelligence-led defense that anticipates threats before they cause harm.</p>
            </div>
          </Reveal>
        </div>

        <div className="mx-auto max-w-7xl px-6 grid gap-6 md:grid-cols-3">
          {values.map((v, i) => (
            <Reveal key={v.title} delay={(i % 3) * 80}>
              <div className="h-full rounded-2xl border border-white/10 bg-brand-navy2/60 p-7 card-hover">
                <div className="grid h-11 w-11 place-items-center rounded-xl bg-brand-lime/10 text-brand-lime"><v.icon className="h-5 w-5" /></div>
                <h4 className="mt-5 font-display text-lg font-semibold text-white">{v.title}</h4>
                <p className="mt-3 text-sm leading-relaxed text-slate-400">{v.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
    </>
  );
}
